import { ChangeDetectorRef, Component, ViewChild, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, takeUntil } from 'rxjs';
import { ToasterMessages } from 'src/app/shared/constants/constants';
import { getmonths } from 'src/app/shared/helpers/dropdown-options';
import { CargoheatingCommonService } from 'src/app/shared/service/cargo-heating-service/cargoheating-common.service';
import { EmissionService } from 'src/app/shared/service/emission-service/emission.service';
import { OverlapService } from 'src/app/shared/service/overlapService/overlap.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { GenerateEmissionReportComponent } from 'src/app/shared/components/generate-emission-report/generate-emission-report.component';
import { DEMO_VESSEL_PC } from 'src/app/shared/helpers/heating-table-dynamic-extract.util';
import { isEmzoilVessel, normalizeEmissionVesselClient } from 'src/app/shared/helpers/emission-client-filter.util';

@Component({
  selector: 'app-imo-dcs',
  templateUrl: './imo-dcs.component.html',
  styleUrls: ['./imo-dcs.component.scss']
})
export class ImoDCSComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;

  myreportForm: FormGroup;
  monthOptions: any;
  vesselnameoptions: any;

  paginatedData: any[] = [];
  currentPage: number = 0;
  itemsPerPage: number = 100;

  elements: any[] = [];
  // tableData: any[] = [];
  vesselNameSuggestions: any[] = [];
  allFleetData: any[] = [];
  clientList: string[] = [];

  clientFilters: { [key: string]: (item: any) => boolean } = {
    'Eiger Shipping': (item) => item.client_name === 'Eiger' && !isEmzoilVessel(item),
    'Zeronorth ZN': (item) => item.client_name === 'ZeroNorth',
    'Elite Shipping': (item) =>
      item.client_name === 'Elite' ||
      item.client_name === 'ELITE TANKSHIP DMCC',
    'Bunge Shipping': (item) => item.client_name === 'Bunge',
    'Alcor Shipping': (item) =>
      item.client_name === 'ALCOR CHARTERING DMCC',
    'EMZoil Shipping': (item) =>
      item.client_name === 'EMZ OIL' ||
      item.client_name === 'EMZOIL FZCO' ||
      item.client_name?.toLowerCase().includes('emz') ||
      isEmzoilVessel(item),
    'Ethiopian Shipping & Logistics': (item) =>
      item.client_name?.toLowerCase() === 'ethiopian shipping & logistics',
  };

  quickPeriods = ['Q1', 'Q2', 'Q3', 'Q4', 'Year', 'YTD'];
  selectedPeriod = '';

  isLoading: boolean = false;
  username: any;
  subusername: any;
  private destroy$ = new Subject<void>();



  reportOptions = [
    { label: 'Average Temp Forecast', value: 'avg_temp_forecast' },
    { label: 'Tank Temp Forecast', value: 'tank_temp_forecast' },
    { label: 'Heat Plan', value: 'heat_plan' },
    { label: 'Bunker Report', value: 'bunker_forecast' }
  ];

  constructor(
    private fb: FormBuilder,
    private toastr: ToastrService,
    private commonService: CargoheatingCommonService,
    private emissionService: EmissionService,
    private router: Router,
    private overlayService: OverlapService,
    private requestFormService: RequestFormService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
  ) {
    this.myreportForm = this.fb.group({
      vesselname: [''],
      vesselIMO: ['', [Validators.pattern('^[0-9]*$')]],
      client: [''],
      fromDate: [null],
      toDate: [null],
    });

    this.paginateData();
  }

  ngOnInit(): void {
    this.username = localStorage.getItem('username') ?? '';
    this.subusername = localStorage.getItem('loginPic') ?? '';
    this.fetchReportTableData();
    this.setupFilterSubscriptions();
  }

  setupFilterSubscriptions(): void {
    this.myreportForm.get('client')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedClient: string) => {
        if (selectedClient && this.clientFilters[selectedClient]) {
          this.vesselNameSuggestions = this.allFleetData.filter(this.clientFilters[selectedClient]);
        } else {
          this.vesselNameSuggestions = this.allFleetData;
        }
        this.myreportForm.get('vesselname')?.setValue([]);
      });

    // Same behaviour as the Emission Report popup: clear To Date if it falls before From Date
    this.myreportForm.get('fromDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((from: Date | null) => {
        const to: Date | null = this.myreportForm.get('toDate')?.value;
        if (from && to && to < from) {
          this.myreportForm.get('toDate')?.setValue(null);
        }
      });
  }

  quickSelect(period: string) {
    const today = new Date();
    const year = today.getFullYear();
    let start = new Date(year, 0, 1);
    let end = new Date(year, 11, 31);

    switch (period) {
      case 'Q1':
        start = new Date(year, 0, 1);
        end = new Date(year, 2, 31);
        break;
      case 'Q2':
        start = new Date(year, 3, 1);
        end = new Date(year, 5, 30);
        break;
      case 'Q3':
        start = new Date(year, 6, 1);
        end = new Date(year, 8, 30);
        break;
      case 'Q4':
        start = new Date(year, 9, 1);
        end = new Date(year, 11, 31);
        break;
      case 'Year':
        start = new Date(year, 0, 1);
        end = new Date(year, 11, 31);
        break;
      case 'YTD':
        start = new Date(year, 0, 1);
        end = new Date(year, today.getMonth(), today.getDate());
        break;
    }
    this.selectedPeriod = period;
    this.myreportForm.patchValue({ fromDate: start, toDate: end });
  }

  private toYmd(date: Date): string {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe((sort: Sort) => {
      this.sortData(sort);
    });
  }

  applyClientFilter(sourceData: any[]) {
    const selectedClient = this.myreportForm.get('client')?.value;

    if (this.username === 'Meraki - Operations') {
      if (selectedClient && this.clientFilters[selectedClient]) {
        this.elements = sourceData.filter(this.clientFilters[selectedClient]);
      } else {
        this.elements = sourceData;
      }
    } else if (this.clientFilters[this.username]) {
      this.elements = sourceData.filter(this.clientFilters[this.username]);
    } else {
      this.elements = sourceData;
    }

    if (this.subusername?.toLowerCase() === 'grace') {
      const graceImos = ['9378876', '9399480', '9438406', '9432048'];
      this.elements = this.elements.filter((item: any) =>
        graceImos.includes(String(item.imo_number ?? item.ship_imo ?? item.imo ?? '').trim())
      );
    }
  }

  fetchReportTableData(isInitialFetch = true, params: any = {}): void {
    this.isLoading = true;

    this.emissionService.getImoDcsData(params).pipe(takeUntil(this.destroy$)).subscribe(
      (res: any) => {
        let rawData = res?.data || [];
        rawData.forEach(normalizeEmissionVesselClient);
        if (this.subusername?.toLowerCase() === 'grace') {
          const graceImos = ['9378876', '9399480', '9438406', '9432048'];
          rawData = rawData.filter(
            (item: any) => graceImos.includes(String(item.imo_number ?? item.ship_imo ?? item.imo ?? '').trim())
          );
        }
        rawData = this.filterAndMaskDemoVessels(rawData);
        if (isInitialFetch) {
          // Extract unique vessels for suggestions
          const uniqueVesselsMap = new Map();
          rawData.forEach((item: any) => {
            if (!uniqueVesselsMap.has(item.imo_number)) {
              uniqueVesselsMap.set(item.imo_number, {
                ship_name: item.vessel_name,
                ship_imo: item.imo_number,
                client_name: item.client_name
              });
            }
          });
          this.allFleetData = Array.from(uniqueVesselsMap.values());
          this.vesselNameSuggestions = this.allFleetData;

          // Extract clients that are present in the data
          this.clientList = Object.keys(this.clientFilters).filter((clientKey) => {
            return this.allFleetData.some((v) => this.clientFilters[clientKey](v));
          });
        }

        this.applyClientFilter(rawData);

        this.currentPage = 0;
        if (this.paginator) {
          this.paginator.firstPage();
        }
        this.paginateData();

        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
        console.error(error);
        this.toastr.error('Failed to load IMO DCS data');
      },
    );
  }

  @ViewChild('paginator') paginator!: any;

  showDetails(row: any) {
    // Navigates to a detailed view if exists.
  }
  onReportSelect(selectedType: string, row: any) {
    const payload = {
      report_type: selectedType,
      vessel_imo: row.ship_imo,
      hp_number: row.hp_np,
      voyage_no: row.voyage_no,
    };

    this.commonService.printFinalReport(payload).subscribe(
      (response: any) => {
        this.overlayService.showPdf(response);
      },
      () => {
        this.toastr.info(ToasterMessages.RecordNotFount);
      }
    );
  }

  sortData(sort: Sort) {
    const data = this.paginatedData.slice();
    if (!sort.active || sort.direction === '') {
      this.paginatedData = data;
      return;
    }

    const isAsc = sort.direction === 'asc';

    this.paginatedData = data.sort((a, b) => {
      const valueA = a[sort.active];
      const valueB = b[sort.active];
      if (valueA == null) return isAsc ? -1 : 1;
      if (valueB == null) return isAsc ? 1 : -1;
      return (valueA < valueB ? -1 : 1) * (isAsc ? 1 : -1);
    });
  }

  paginateData() {
    const startIndex = this.currentPage * this.itemsPerPage;
    this.paginatedData = this.elements.slice(startIndex, startIndex + this.itemsPerPage);
  }

  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex;
    this.itemsPerPage = event.pageSize;
    this.paginateData();
  }

  filterClick() {
    const vesselIMO = this.myreportForm.get('vesselIMO')?.value?.trim() || '';
    const vesselNameVal = this.myreportForm.get('vesselname')?.value;
    const fromDate = this.myreportForm.get('fromDate')?.value;
    const toDate = this.myreportForm.get('toDate')?.value;

    if (!!fromDate !== !!toDate) {
      this.toastr.warning('Please select a valid date range');
      return;
    }

    let params: any = {};
    if (vesselIMO) params.imo_number = vesselIMO;

    if (Array.isArray(vesselNameVal)) {
      const names = vesselNameVal.filter(n => n && n.trim() !== '');
      if (names.length > 0) {
        params.vessel_name = names.join(',');
      }
    } else if (vesselNameVal && typeof vesselNameVal === 'string') {
      params.vessel_name = vesselNameVal.trim();
    }

    if (fromDate && toDate) {
      params.year = new Date(fromDate).getFullYear();
      params.start_time = `${this.toYmd(fromDate)}T00:00:00Z`;
      params.end_time = `${this.toYmd(toDate)}T23:59:59Z`;
    }

    this.fetchReportTableData(false, params);
  }

  formatEeoi(val: any): string {
    if (val === null || val === undefined || val === '') return '-';
    const num = parseFloat(val);
    if (isNaN(num)) return val;
    // Round to 5 decimal places and remove trailing zeros
    return Number(num.toFixed(6)).toString();
  }

  downloadClick() {
    const parseDate = (dateString: any) => {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return `${date.getDate().toString().padStart(2, '0')}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, '0')}-${date.getFullYear()}`;
      }
      return '';
    };

    const data = this.paginatedData;

    const csvHeader = 'S.No,Ship Name,Ship IMO,Report Type,Voyage Month,Ingested Time';

    const csvData = data
      .map((row: any, index: number) => {
        const ingested = row.ingested_time ? parseDate(row.ingested_time) : '';
        return `${index + 1},${row.ship_name},${row.ship_imo},${row.report_type},${row.voyage_month},${ingested}`;
      })
      .join('\n');

    const csvContent = `${csvHeader}\n${csvData}`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'final_report_data.csv';
    a.click();
  }

  clearClick() {
    this.myreportForm.reset();
    this.selectedPeriod = '';
    this.fetchReportTableData();
  }

  goBack() {
    this.router.navigate(['/ch-graph']);
  }

  isDarkMode() {
    return this.requestFormService.isDarkMode();
  }


  filterShipNames(event: any) {
    const input = event.target.value.toLowerCase();
    this.vesselNameSuggestions = this.vesselnameoptions.filter((item: any) =>
      item.ship_name.toLowerCase().includes(input)
    );
  }
  refreshPageData() {
    this.fetchReportTableData();
  }

  printFinalReport(row: any) {
    const payload = {
      vessel_imo: row.ship_imo,
      voyage_no: row.voyage_no
    };

    this.commonService.printFinalReport(payload).subscribe(
      (res: any) => {
        this.overlayService.showPdf(res);
      },
      () => {
        this.toastr.error('Report Not Found');
      }
    );
  }

  openGenerateReportPopup(row: any) {
    this.dialog.open(GenerateEmissionReportComponent, {
      width: '560px',
      data: { ...row, reportType: 'IMO-DCS' },
      disableClose: true,
    });
  }
  private isDemoUser(): boolean {
    const username = (localStorage.getItem('username') || '').toLowerCase();
    const loginPic = (localStorage.getItem('loginPic') || '').toLowerCase();
    const subuser = (this.subusername || '').toLowerCase();
    return subuser === 'demo' || loginPic === 'demo' || username === 'meraki - demo' || username.includes('demo');
  }

  private filterAndMaskDemoVessels(vessels: any[]): any[] {
    if (!this.isDemoUser()) {
      return vessels;
    }

    return (vessels || [])
      .filter((v: any) =>
        DEMO_VESSEL_PC.includes(
          String(v?.imo_number ?? v?.ship_imo ?? '').trim()
        )
      )
      .map((v: any) => {
        const imo = String(
          v?.imo_number ?? v?.ship_imo ?? ''
        ).trim();

        const index = DEMO_VESSEL_PC.indexOf(imo);

        return {
          ...v,

          // Mask vessel name
          vessel_name: `Vessel ${index + 1}`,
          ship_name: `Vessel ${index + 1}`,
          client_name: 'Client 1',
          // Mask IMO
          imo_number: 'xxxxxxx',
          ship_imo: 'xxxxxxx'
        };
      });
  }

  get isMeraki(): boolean {
    return this.username === 'Meraki - Operations';
  }

  formatStartDate(dateVal: any): string {
    if (!dateVal) return '-';
    // If dateVal is string or Date
    let d: Date;
    if (typeof dateVal === 'string') {
      // Split YYYY-MM-DD or take first 10 chars
      const parts = dateVal.split('T')[0].split(' ')[0].split('-');
      if (parts.length === 3) {
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const day = parseInt(parts[2], 10);
        d = new Date(year, month, day);
      } else {
        d = new Date(dateVal);
      }
    } else {
      d = new Date(dateVal);
    }

    if (isNaN(d.getTime())) return '-';

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = d.getDate();
    const month = months[d.getMonth()];
    return `${day} ${month}`;
  }
}
